// src/components/ProcessedDocumentResultsViewer.tsx

import {
  DownloadOutlined,
  EditOutlined,
  HistoryOutlined,
  MenuOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Layout, List, Menu, Space, Spin, Tooltip, Typography } from "antd";
import axios from "axios";
import { type Change, diffLines } from "diff";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import type { MenuInfo } from "rc-menu/lib/interface";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomerServiceRequest from "@/components/CustomerServiceRequest";
import ErrorBoundary from "@/components/ErrorBoundary";
import { notify } from "@/components/Notification";
import { USE_VERBOSE_LOGGING } from "@/lib/constants";
import useAppStore from "@/store/useAppStore";
import type { FileData, FileRevision, ProcessingJob } from "@/types";
import { handleDownload, safeStringify } from "@/utils/api";
import { isEligibleForSupport } from "@/utils/documentUtils";

const { Content } = Layout;
const { Text } = Typography;

const PDFViewer = dynamic(() => import("@/components/PDFViewer"), { ssr: false });
const MarkdownViewer = dynamic(() => import("@/components/MarkdownViewer"), { ssr: false });
const PlaintextViewer = dynamic(() => import("@/components/PlaintextViewer"), { ssr: false });
const HTMLwithJSViewer = dynamic(() => import("@/components/HTMLwithJSViewer"), { ssr: false });
const CSVtableViewer = dynamic(() => import("@/components/CSVtableViewer"), { ssr: false });
const MarkdownEditor = dynamic(() => import("@/components/MarkdownEditor"), { ssr: false });
const AnkiDeckViewer = dynamic(() => import("@/components/AnkiDeckViewer"), { ssr: false });

interface JobSummary {
  job_id: string;
  document_id: string;
  status: string;
  progress_percentage: number;
  update_time: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  page_count: number;
  word_count: number;
  processing_time: number;
  total_input_tokens: number;
  total_output_tokens: number;
  processing_cost: number;
  optimization_cost: number;
  generation_flags: {
    requested: Record<string, boolean>;
    completed: Record<string, boolean>;
  };
  token_usage: Record<string, { input_tokens: number; output_tokens: number }>;
  [key: string]: any;
}

interface ProcessedDocumentResultsViewerProps {
  job_id: string;
  siderWidth?: number;
  onEditMarkdown?: (content: string, filename: string) => void;
  onUpdateFiles: (updatedFiles: FileData[]) => void;
  initialActiveFilename?: string;
  onGenerateMore?: () => void;
  onClose?: () => void;
}

interface EditingState {
  content: string;
  filename: string;
  originalContent: string;
}

const debugLog = (...args: unknown[]) => {
  if (!USE_VERBOSE_LOGGING) return;
  console.log(...args);
};

const debugWarn = (...args: unknown[]) => {
  if (!USE_VERBOSE_LOGGING) return;
  console.warn(...args);
};

async function getHash(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

const ProcessedDocumentResultsViewer: React.FC<ProcessedDocumentResultsViewerProps> = ({
  job_id,
  onEditMarkdown,
  onUpdateFiles,
  initialActiveFilename,
  onGenerateMore,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const editingStateRef = useRef<EditingState | null>(null);
  const [revisionsDrawerVisible, setRevisionsDrawerVisible] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState<FileRevision | null>(null);
  const [optimisticUpdates, setOptimisticUpdates] = useState<Record<string, string>>({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [hasRevisions, setHasRevisions] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled] = useState(true);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const {
    error: storeError,
    isProcessingDocument,
    forceRefreshJobStatus,
    jobStatus,
    scrollToResults,
    setScrollToResults,
  } = useAppStore();
  const [jobSummary, setJobSummary] = useState<JobSummary | null>(null);
  const [jobSummaryLoading, setJobSummaryLoading] = useState(false);
  const [jobSummaryError, setJobSummaryError] = useState<Error | null>(null);
  const [isValidatingJobSummary, setIsValidatingJobSummary] = useState(false);
  const jobSummaryStatus = jobSummary?.status;

  const [filesData, setFilesData] = useState<FileData[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<Error | null>(null);

  const [fileRevisionsData, setFileRevisionsData] = useState<FileRevision[]>([]);
  const [fileRevisionsLoading, setFileRevisionsLoading] = useState(false);
  const [fileRevisionsError, setFileRevisionsError] = useState<Error | null>(null);

  const [currentFilenameForRevisions, setCurrentFilenameForRevisions] = useState<string | null>(
    null,
  );

  const fetchJobSummary = useCallback(async () => {
    if (!job_id) return;
    setJobSummaryLoading(true);
    setIsValidatingJobSummary(true);
    try {
      const res = await fetch(`/api/document_job_summary/${encodeURIComponent(job_id)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to fetch job summary");
      }
      const data = await res.json();
      setJobSummary(data);
      setJobSummaryError(null);
    } catch (err: any) {
      console.error("Error fetching job summary:", safeStringify(err));
      setJobSummaryError(err);
    } finally {
      setJobSummaryLoading(false);
      setIsValidatingJobSummary(false);
    }
  }, [job_id]);

  const fetchFiles = useCallback(async () => {
    if (!job_id) {
      debugLog("[ProcessedDocumentViewer] No job_id provided for fetchFiles");
      return;
    }

    debugLog("[ProcessedDocumentViewer] Starting fetchFiles for job:", job_id);
    setFilesLoading(true);
    setFetchError(null);

    try {
      const timestamp = new Date().getTime();
      const res = await fetch(
        `/api/get_processed_files/${encodeURIComponent(job_id)}?checkLatest=true&t=${timestamp}`,
        {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
          },
          cache: "no-store",
        },
      );

      if (res.status === 304) {
        debugLog("[ProcessedDocumentViewer] Files not modified (304)");
        setFilesError(null);
        setFilesLoading(false);
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error("[ProcessedDocumentViewer] Failed to fetch files:", data);
        throw new Error(data.error || "Failed to fetch files");
      }

      const data = await res.json();
      debugLog("[ProcessedDocumentViewer] Fetched files count:", data.length);

      data.forEach((file: FileData) => {
        if (file.filename.endsWith(".md")) {
          debugLog("[ProcessedDocumentViewer] Received markdown file:", {
            filename: file.filename,
            contentLength: file.content.length,
            contentPreview: file.content.substring(0, 200),
          });
        }
      });

      const updatedData = data.map((file: FileData) => {
        if (optimisticUpdates[file.filename]) {
          debugLog("[ProcessedDocumentViewer] Applying optimistic update for:", file.filename);
          debugLog("[ProcessedDocumentViewer] Optimistic content:", {
            filename: file.filename,
            originalLength: file.content.length,
            updatedLength: optimisticUpdates[file.filename].length,
            originalPreview: file.content.substring(0, 200),
            updatedPreview: optimisticUpdates[file.filename].substring(0, 200),
          });
          return {
            ...file,
            content: optimisticUpdates[file.filename],
          };
        }

        if (file.filename.endsWith(".md")) {
          debugLog("[ProcessedDocumentViewer] Using server content for:", {
            filename: file.filename,
            contentLength: file.content.length,
            contentPreview: file.content.substring(0, 200),
          });
        }

        return file;
      });

      setFilesData(updatedData);
      setFilesError(null);
      setFetchError(null);
      debugLog("[ProcessedDocumentViewer] Files data updated successfully");

      updatedData.forEach((file: FileData) => {
        if (file.filename.endsWith(".md")) {
          debugLog("[ProcessedDocumentViewer] Final file state:", {
            filename: file.filename,
            contentLength: file.content.length,
            contentPreview: file.content.substring(0, 200),
            hasOptimisticUpdate: !!optimisticUpdates[file.filename],
          });
        }
      });
    } catch (err: any) {
      console.error("[ProcessedDocumentViewer] Error in fetchFiles:", err);
      debugLog("[ProcessedDocumentViewer] Error details:", safeStringify(err));
      setFilesError(err);
    } finally {
      setFilesLoading(false);
      debugLog("[ProcessedDocumentViewer] fetchFiles completed");
    }
  }, [job_id, optimisticUpdates]);

  const fetchFileRevisions = useCallback(async () => {
    if (!job_id || !currentFilenameForRevisions) return;
    setFileRevisionsLoading(true);
    try {
      const url = `/api/file_revisions/${encodeURIComponent(job_id)}?filename=${encodeURIComponent(currentFilenameForRevisions)}`;
      const res = await fetch(url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to fetch file revisions");
      }
      const data = await res.json();
      setFileRevisionsData(data);
      setFileRevisionsError(null);
    } catch (err: any) {
      console.error("Error fetching file revisions:", safeStringify(err));
      setFileRevisionsError(err);
    } finally {
      setFileRevisionsLoading(false);
    }
  }, [job_id, currentFilenameForRevisions]);

  useEffect(() => {
    return () => {
      setEditMode(false);
      setEditingState(null);
      editingStateRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!job_id || !autoRefreshEnabled || editMode) {
      return;
    }
    if (!jobSummaryStatus || jobSummaryStatus === "completed") {
      return;
    }

    const intervalId = setInterval(() => {
      fetchJobSummary().catch(() => {});
      fetchFiles().catch(() => {});
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [job_id, jobSummaryStatus, autoRefreshEnabled, editMode, fetchJobSummary, fetchFiles]);

  useEffect(() => {
    if (job_id) {
      fetchJobSummary().catch(() => {});
      fetchFiles().catch(() => {});
    }
  }, [job_id, fetchJobSummary, fetchFiles]);

  useEffect(() => {
    if (currentFilenameForRevisions) {
      fetchFileRevisions().catch(() => {});
    }
  }, [currentFilenameForRevisions, fetchFileRevisions]);

  useEffect(() => {
    if (fileRevisionsData && currentFilenameForRevisions && activeTab) {
      const revisionsForCurrentFile = fileRevisionsData.filter(
        (revision) => revision.filename === activeTab,
      );
      setHasRevisions(revisionsForCurrentFile.length > 0);
    } else {
      setHasRevisions(false);
    }
  }, [fileRevisionsData, currentFilenameForRevisions, activeTab]);

  useEffect(() => {
    return () => {
      setOptimisticUpdates({});
    };
  }, []);

  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing) return;
    try {
      setIsRefreshing(true);
      setFetchError(null);
      await forceRefreshJobStatus();
      await fetchJobSummary();
      await fetchFiles();
      if (currentFilenameForRevisions) await fetchFileRevisions();
    } catch (error) {
      console.error("Error refreshing files:", safeStringify(error));
    } finally {
      setIsRefreshing(false);
    }
  }, [
    isRefreshing,
    forceRefreshJobStatus,
    fetchJobSummary,
    fetchFiles,
    currentFilenameForRevisions,
    fetchFileRevisions,
  ]);

  const hasRefreshedOnMount = useRef(false);
  useEffect(() => {
    if (job_id && !hasRefreshedOnMount.current) {
      hasRefreshedOnMount.current = true;
      handleManualRefresh();
    }
  }, [handleManualRefresh, job_id]);

  const displayNames: Record<string, string> = useMemo(
    () => ({
      "optimized_document.md": "Optimized Formatted Document (Markdown)",
      "optimized_document.html": "Optimized Formatted Document (HTML for Emails/Web)",
      "optimized_document.pdf": "Optimized Formatted Document (PDF)",
      "graded_multiple_choice_quiz.html": "Graded Multiple Choice Quiz (HTML)",
      "graded_multiple_choice_quiz.pdf": "Multiple Choice Quiz with Answer Key (PDF)",
      "multiple_choice_quiz.md": "Generated Multiple Choice Quiz (Markdown)",
      "short_answer_quiz.md": "Generated Short Answer Quiz (Markdown)",
      "short_answer_quiz.html": "Generated Short Answer Quiz (HTML)",
      "anki_flashcards.apkg": "Anki Flashcards (Ready for Import)",
      "flash_cards.csv": "Anki Flashcards (CSV)",
      "presentation_slides.pdf": "Presentation Slides (PDF)",
      "presentation_slides.html": "Presentation Slides (HTML for Emails/Web)",
      "presentation_slides.tex": "Presentation Slides (LaTeX)",
      "presentation_slides.md": "Presentation Slide Text Content to Edit (Markdown)",
      "summary.md": "Document Summary (Markdown)",
      "summary.html": "Document Summary (HTML for Emails/Web)",
      "summary.pdf": "Document Summary (PDF)",
      "mindmap_outline.md": "Mindmap Outline (Markdown)",
      "mindmap_outline.html": "Mindmap Outline (HTML for Emails/Web)",
      "mindmap_outline.pdf": "Mindmap Outline (PDF)",
      "mindmap.html": "Mindmap Diagram (HTML for Emails/Web)",
      "mermaid_mindmap.txt": "Mindmap (Mermaid Diagram Format)",
      "lesson_plan.md": "Lesson Plan (Markdown)",
      "lesson_plan.html": "Lesson Plan (HTML for Emails/Web)",
      "lesson_plan.pdf": "Lesson Plan (PDF)",
      "readability_heatmap.html": "Readability Analysis Report (HTML)",
      "grade_level_version_elementary.md": "Grade Level Version - Elementary (Markdown)",
      "grade_level_version_elementary.html": "Grade Level Version - Elementary (HTML)",
      "grade_level_version_intermediate.md": "Grade Level Version - Intermediate (Markdown)",
      "grade_level_version_intermediate.html": "Grade Level Version - Intermediate (HTML)",
      "grade_level_version_high_school.md": "Grade Level Version - High School (Markdown)",
      "grade_level_version_high_school.html": "Grade Level Version - High School (HTML)",
      "grade_level_version_college.md": "Grade Level Version - College (Markdown)",
      "grade_level_version_college.html": "Grade Level Version - College (HTML)",
      "grade_level_version_academic.md": "Grade Level Version - Academic (Markdown)",
      "grade_level_version_academic.html": "Grade Level Version - Academic (HTML)",
      "extracted_text.txt": "Raw Extracted Text Before Corrections (Plain Text)",
    }),
    [],
  );

  const decodeContent = useCallback((content: string, filename: string): string => {
    debugLog("[ProcessedDocumentViewer] Decoding content for:", filename);
    debugLog("[ProcessedDocumentViewer] Content length before decode:", content.length);

    const lower = filename.toLowerCase();
    const isBinary = lower.endsWith(".pdf") || lower.endsWith(".apkg");

    if (isBinary) {
      return content;
    }

    try {
      return atob(content);
    } catch (error) {
      console.error("[ProcessedDocumentViewer] Decode error:", error);
      return content;
    }
  }, []);

  const filteredFiles = useMemo(() => {
    const validExtensions = [".md", ".html", ".pdf", ".txt", ".csv", ".json", ".apkg", ".tex"];

    const getFileOrder = (filename: string): number => {
      let baseOrder = 100;
      if (filename.includes("optimized_document")) baseOrder = 100;
      else if (filename.includes("presentation_slides")) baseOrder = 200;
      else if (filename.includes("quiz")) baseOrder = 300;
      else if (filename.includes("summary")) baseOrder = 400;
      else if (filename.includes("mindmap")) baseOrder = 500;
      else if (filename.includes("lesson_plan")) baseOrder = 600;
      else if (filename.includes("readability")) baseOrder = 700;
      else if (filename.includes("grade_level")) baseOrder = 800;
      else if (filename.includes("flash")) baseOrder = 900;
      else baseOrder = 1000;

      let extensionOrder = 10;
      if (filename.endsWith(".md")) extensionOrder = 1;
      else if (filename.endsWith(".html")) extensionOrder = 2;
      else if (filename.endsWith(".pdf")) extensionOrder = 3;
      else if (filename.endsWith(".tex")) extensionOrder = 4;
      else extensionOrder = 5;

      return baseOrder + extensionOrder;
    };

    const sortedFiles = [...filesData].sort(
      (a, b) => getFileOrder(a.filename) - getFileOrder(b.filename),
    );
    return sortedFiles.filter((file) => {
      if (file.filename === "multiple_choice_quiz.html") return false;
      if (file.filename.toLowerCase().endsWith("__mobile.pdf")) return false;
      return validExtensions.some((ext) => file.filename.toLowerCase().endsWith(ext));
    });
  }, [filesData]);

  const currentFileContent = useMemo(() => {
    if (!activeTab || !filteredFiles.length) return null;
    const f = filteredFiles.find((file) => file.filename === activeTab);
    return f ? f.content : null;
  }, [activeTab, filteredFiles]);

  useEffect(() => {
    let mounted = true;
    if (initialActiveFilename && filteredFiles.some((f) => f.filename === initialActiveFilename)) {
      if (mounted) {
        setActiveTab(initialActiveFilename);
      }
    } else if (filteredFiles.length > 0 && !activeTab) {
      if (mounted) {
        setActiveTab(filteredFiles[0].filename);
      }
    }
    return () => {
      mounted = false;
    };
  }, [filteredFiles, initialActiveFilename, activeTab]);

  const handleTabChange = useCallback((newActiveTab: string) => {
    setActiveTab(newActiveTab);
    setEditMode(false);
    setMobileMenuVisible(false);
    setSelectedRevision(null);
    setCurrentFilenameForRevisions(null);
  }, []);

  const handleDownloadZipWrapper = useCallback(async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      await handleDownload(job_id);
      notify.success({ content: "Your download has started." });
    } catch (error) {
      console.error("Error downloading zip file:", safeStringify(error));
      notify.error({ content: "Failed to download zip. Please try again later." });
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, job_id]);

  const handleEdit = useCallback(
    async (filename: string) => {
      debugLog("[ProcessedDocumentViewer] Starting edit for file:", filename);
      const file = filteredFiles.find((f) => f.filename === filename);
      if (!file) {
        debugWarn(`[ProcessedDocumentViewer] No file found with filename: ${filename}`);
        return;
      }

      try {
        const decoded = decodeContent(file.content, filename);
        debugLog("[ProcessedDocumentViewer] Decoded content preview:", decoded.substring(0, 200));
        debugLog("[ProcessedDocumentViewer] Decoded content length:", decoded.length);

        const newEditingState = {
          content: decoded,
          filename,
          originalContent: decoded,
        };

        setEditingState(newEditingState);
        editingStateRef.current = newEditingState;
        debugLog("[ProcessedDocumentViewer] Editing state set successfully");

        setTimeout(() => {
          setEditMode(true);
          debugLog("[ProcessedDocumentViewer] Edit mode activated");
        }, 0);

        if (onEditMarkdown) {
          onEditMarkdown(decoded, filename);
          debugLog("[ProcessedDocumentViewer] Edit callback triggered");
        }
      } catch (error) {
        console.error("[ProcessedDocumentViewer] Error preparing file for editing:", error);
        debugLog(
          "[ProcessedDocumentViewer] Failed file content preview:",
          file.content.substring(0, 200),
        );
        notify.error({ content: "Failed to prepare file for editing" });
      }
    },
    [filteredFiles, decodeContent, onEditMarkdown],
  );

  const handleSaveMarkdown = useCallback(
    async (newContent: string) => {
      debugLog("[ProcessedDocumentViewer] Starting save markdown process");
      debugLog("[ProcessedDocumentViewer] New content preview:", newContent.substring(0, 200));
      debugLog("[ProcessedDocumentViewer] New content length:", newContent.length);

      if (!newContent?.trim()) {
        debugWarn("[ProcessedDocumentViewer] Attempted to save empty content");
        notify.error({ content: "Cannot save empty content" });
        return;
      }

      if (!job_id || !activeTab) {
        console.error("[ProcessedDocumentViewer] Missing job info:", { job_id, activeTab });
        notify.error({ content: "Unable to save - missing job information" });
        return;
      }

      if (!editingStateRef.current) {
        console.error("[ProcessedDocumentViewer] Missing editing state");
        notify.error({ content: "Unable to save - editor state not found" });
        return;
      }

      const { filename } = editingStateRef.current;
      debugLog("[ProcessedDocumentViewer] Current editing state:", {
        filename,
        contentLength: editingStateRef.current.content.length,
        contentPreview: editingStateRef.current.content.substring(0, 200),
      });

      const originalHash = await getHash(newContent);
      debugLog("[ProcessedDocumentViewer] Original content hash:", originalHash);

      setIsSaving(true);
      setProcessingFiles(true);

      try {
        const encoder = new TextEncoder();
        const utf8Bytes = encoder.encode(newContent);
        const encodedContent = Buffer.from(utf8Bytes).toString("base64");
        debugLog("[ProcessedDocumentViewer] Encoded content length:", encodedContent.length);

        setOptimisticUpdates((prev) => {
          debugLog("[ProcessedDocumentViewer] Setting optimistic update for:", filename);
          return {
            ...prev,
            [filename]: encodedContent,
          };
        });

        debugLog("[ProcessedDocumentViewer] Sending update request to /api/update_markdown");
        const saveResponse = await axios.post(
          "/api/update_markdown",
          {
            job_id,
            filename,
            content: encodedContent,
          },
          {
            timeout: 30000,
          },
        );

        debugLog("[ProcessedDocumentViewer] Update response:", saveResponse.data);

        if (!saveResponse.data?.success) {
          throw new Error(saveResponse.data?.error || "Failed to save markdown changes");
        }

        debugLog("[ProcessedDocumentViewer] Update successful, updating files data");
        setFilesData((prev) =>
          prev.map((f) => (f.filename === filename ? { ...f, content: encodedContent } : f)),
        );

        debugLog("[ProcessedDocumentViewer] Starting file regeneration with plain content");
        const regenerateResponse = await axios.post(
          `/api/regenerate_files`,
          {
            job_id,
            filename,
            markdownContent: newContent,
          },
          {
            timeout: 30000,
          },
        );

        debugLog("[ProcessedDocumentViewer] Regeneration response:", regenerateResponse.data);

        if (!regenerateResponse.data?.regenerationComplete) {
          throw new Error("File regeneration did not complete successfully");
        }

        debugLog("[ProcessedDocumentViewer] Waiting for FastAPI processing...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        debugLog("[ProcessedDocumentViewer] Delay complete, fetching updated files");

        const response = await fetch(
          `/api/get_processed_files/${encodeURIComponent(job_id)}?checkLatest=true`,
        );
        const updatedFiles = (await response.json()) as Array<{
          filename: string;
          content: string;
        }>;
        const updatedFile = updatedFiles.find((f) => f.filename === filename);
        if (updatedFile) {
          const updatedContent = atob(updatedFile.content);
          const updatedHash = await getHash(updatedContent);
          debugLog("[ProcessedDocumentViewer] Updated content hash:", updatedHash);
          debugLog("[ProcessedDocumentViewer] Content changed:", originalHash !== updatedHash);

          if (originalHash === updatedHash) {
            debugWarn(
              "[ProcessedDocumentViewer] Warning: Content hash unchanged after regeneration!",
            );
          }
        }

        notify.success({
          content: "Markdown updated, files regenerated, and ZIP updated successfully",
        });

        setEditMode(false);
        setEditingState(null);
        editingStateRef.current = null;

        debugLog("[ProcessedDocumentViewer] Cleaning up optimistic updates");
        setOptimisticUpdates((prev) => {
          const { [filename]: removed, ...rest } = prev;
          return rest;
        });

        debugLog("[ProcessedDocumentViewer] Fetching updated files");
        await fetchFiles();
      } catch (error) {
        console.error("[ProcessedDocumentViewer] Error in save process:", error);
        debugLog("[ProcessedDocumentViewer] Failed content preview:", newContent.substring(0, 200));

        setOptimisticUpdates((prev) => {
          debugLog("[ProcessedDocumentViewer] Removing failed optimistic update");
          const { [filename]: removed, ...rest } = prev;
          return rest;
        });

        await fetchFiles();

        if (error instanceof Error) {
          notify.error({ content: `Save failed: ${error.message}` });
        } else {
          notify.error({ content: "Failed to save changes" });
        }

        throw error;
      } finally {
        setIsSaving(false);
        setProcessingFiles(false);
        debugLog("[ProcessedDocumentViewer] Save process completed");
      }
    },
    [job_id, activeTab, fetchFiles],
  );

  const handleViewRevisions = useCallback(async (filename: string) => {
    setCurrentFilenameForRevisions(filename);
    setRevisionsDrawerVisible(true);
  }, []);

  const handleRevertToRevision = useCallback(
    async (revision: FileRevision) => {
      if (!activeTab) return;
      if (!job_id || typeof job_id !== "string") return;
      if (!revision || !revision.id) return;
      try {
        const revertRes = await fetch("/api/revert_to_revision", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            job_id: job_id,
            filename: activeTab,
            revisionId: revision.id,
          }),
        });

        if (!revertRes.ok) {
          const errData = await revertRes.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to revert. Please try again.");
        }

        setRevisionsDrawerVisible(false);

        await fetchFiles();
        await fetchJobSummary();
        if (currentFilenameForRevisions) await fetchFileRevisions();

        const revertedFile = filesData.find((f) => f.filename === activeTab);
        if (revertedFile) {
          const newDecoded = decodeContent(revertedFile.content, activeTab);
          if (editingState) {
            setEditingState({
              ...editingState,
              content: newDecoded,
              originalContent: newDecoded,
            });
          }
        }
      } catch (error) {
        console.error("Error reverting to revision:", safeStringify(error));
      }
    },
    [
      job_id,
      activeTab,
      fetchFiles,
      fetchJobSummary,
      decodeContent,
      filesData,
      currentFilenameForRevisions,
      fetchFileRevisions,
      editingState,
    ],
  );

  const renderDiff = useCallback(
    (oldContent: string | undefined, newContent: string | undefined) => {
      const safeOldContent = oldContent || "";
      const safeNewContent = newContent || "";
      const changes = diffLines(safeOldContent, safeNewContent);
      return (
        <div className="diff-view font-mono text-sm">
          {changes.map((change: Change, index: number) => (
            <pre
              key={index}
              className={`diff-line transition-all duration-200 ease-in-out ${
                change.added
                  ? "bg-green-100 text-green-800"
                  : change.removed
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
              } p-1 whitespace-pre-wrap break-words rounded-sm`}
            >
              {change.added && "+ "}
              {change.removed && "- "}
              {change.value}
            </pre>
          ))}
        </div>
      );
    },
    [],
  );

  const renderRevisionContent = useCallback(
    (revision: FileRevision, index: number, revisions: FileRevision[]) => {
      if (index === 0) {
        const currentFile = filesData.find((f) => f.filename === activeTab);
        if (currentFile) {
          const decodedFileContent = new TextDecoder("utf-8").decode(
            Uint8Array.from(atob(currentFile.content), (c) => c.charCodeAt(0)),
          );
          return renderDiff(revision.content, decodedFileContent);
        }
      }
      if (index < revisions.length - 1) {
        const previousContent = revisions[index + 1].content;
        return renderDiff(previousContent, revision.content);
      }
      return (
        <pre className="diff-line bg-gray-100 text-gray-800 p-1 whitespace-pre-wrap break-words rounded-sm transition-colors duration-200 ease-in-out">
          {revision.content}
        </pre>
      );
    },
    [filesData, activeTab, renderDiff],
  );

  const normalizeStatus = (status: string): string => {
    return status.endsWith("_") ? status.slice(0, -1) : status;
  };

  const renderCustomerServiceRequest = useCallback(() => {
    if (!jobStatus) return null;
    const job: ProcessingJob = {
      id: -1,
      job_id: job_id,
      status: jobStatus.status,
      progress_percentage: jobStatus.progress_percentage,
      error_message: jobStatus.error_message,
      timestamp: jobStatus.created_at,
      created_at: jobStatus.created_at,
      job_status: jobStatus,
      user_email: "",
      document_id: "",
      generate_multiple_choice_quiz: false,
      generate_short_answer_quiz: false,
      generate_anki_flashcards: false,
      generate_presentation_slides: false,
      generate_summary: false,
      generate_mindmap: false,
      generate_readability_analysis: false,
      generate_lesson_plan: false,
      lesson_plan_target_audience: null,
      presentation_theme: null,
      completed_at: null,
      result_file_path: null,
    };
    const jobForEligibility = { ...job, status: normalizeStatus(job.status) };
    const isEligible = isEligibleForSupport(jobForEligibility, filesData);
    if (isEligible) {
      return (
        <Tooltip title="Request support for this job">
          <CustomerServiceRequest jobId={job_id} job={job} processedFiles={filesData} />
        </Tooltip>
      );
    }
    return null;
  }, [jobStatus, job_id, filesData]);

  const renderMobileMenu = useCallback(
    () => (
      <Drawer
        title={
          <div className="flex justify-between items-center">
            <span className="font-semibold text-lg">Select File</span>
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        className="mobile-drawer"
        style={{}}
      >
        <Menu
          mode="inline"
          selectedKeys={[activeTab || ""]}
          onClick={(info: MenuInfo) => {
            handleTabChange(info.key.toString());
          }}
          className="mobile-menu"
          style={{
            width: "100%",
            padding: "8px 0",
          }}
        >
          {filteredFiles.map((file) => (
            <Menu.Item
              key={file.filename}
              className="mobile-menu-item hover:bg-gray-100 transition-all duration-200 ease-in-out rounded-md mx-2 my-1"
              style={{
                whiteSpace: "normal",
                height: "auto",
                lineHeight: "1.5",
                padding: "16px 24px",
                marginBottom: "8px",
              }}
            >
              <div className="mobile-menu-text break-words font-medium">
                {displayNames[file.filename] || file.filename}
              </div>
            </Menu.Item>
          ))}
        </Menu>
      </Drawer>
    ),
    [mobileMenuVisible, activeTab, filteredFiles, handleTabChange, displayNames],
  );

  useEffect(() => {
    if (scrollToResults && filteredFiles.length > 0) {
      const resultsSection = document.querySelector(".processed-results-section");
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        setScrollToResults(false);
      }
    }
  }, [scrollToResults, filteredFiles.length, setScrollToResults]);

  useEffect(() => {
    if (jobSummaryError) {
      console.error("Error fetching job summary:", jobSummaryError.message);
      setFetchError("Unable to load job summary. Please try refreshing the page.");
    } else if (filesError) {
      console.error("Error fetching files:", filesError.message);
      setFetchError("Unable to load generated files. Please try refreshing.");
    } else if (fileRevisionsError) {
      console.error("Error fetching file revisions:", fileRevisionsError.message);
    } else if (filesData.length > 0) {
      setFetchError(null);
    }
  }, [jobSummaryError, filesError, fileRevisionsError, filesData]);

  useEffect(() => {
    if (activeTab) {
      setCurrentFilenameForRevisions(activeTab);
    }
  }, [activeTab]);

  const prevFilesRef = useRef<string | null>(null);

  useEffect(() => {
    const serializedFiles = JSON.stringify(filesData);
    if (prevFilesRef.current !== serializedFiles) {
      prevFilesRef.current = serializedFiles;
      if (onUpdateFiles && typeof onUpdateFiles === "function") {
        onUpdateFiles(filesData);
      }
    }
  }, [filesData, onUpdateFiles]);

  const prepareAnkiFile = useCallback((content: string, filename: string): File => {
    const binaryString = atob(content);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/octet-stream" });
    return new File([blob], filename, { type: "application/octet-stream" });
  }, []);

  const ankiFile = useMemo(() => {
    if (!activeTab || !currentFileContent || !activeTab.endsWith(".apkg")) return null;
    return prepareAnkiFile(currentFileContent, activeTab);
  }, [activeTab, currentFileContent, prepareAnkiFile]);

  const renderFileContent = useCallback(
    (filename: string, content: string | null) => {
      if (!content) return null;

      const actualContent = optimisticUpdates[filename] || content;
      const lower = filename.toLowerCase();
      const isBinary = lower.endsWith(".pdf") || lower.endsWith(".apkg");

      const decodedContent = isBinary
        ? actualContent
        : new TextDecoder("utf-8").decode(
            Uint8Array.from(atob(actualContent), (c) => c.charCodeAt(0)),
          );

      if (filename.endsWith(".pdf")) {
        return (
          <div className="border border-gray-200 rounded-md overflow-hidden shadow-sm transition-all duration-200 ease-in-out">
            <PDFViewer url={`data:application/pdf;base64,${decodedContent}`} />
          </div>
        );
      }

      if (filename.endsWith(".md")) {
        if (editMode && editingState) {
          const editState = editingStateRef.current;
          if (!editState || !activeTab || editState.filename !== filename) {
            return <Spin size="large" />;
          }
          return (
            <div className="markdown-editor-container bg-white rounded-md shadow-lg p-4 transition-shadow duration-200 ease-in-out">
              <MarkdownEditor
                initialContent={decodedContent}
                onSave={handleSaveMarkdown}
                onCancel={() => setEditMode(false)}
                editingFile={activeTab || ""}
              />
            </div>
          );
        }
        return (
          <div className="rounded-md border border-gray-200 p-4 shadow-sm bg-white transition-all duration-200 ease-in-out">
            <MarkdownViewer content={decodedContent} filename={filename} />
          </div>
        );
      }

      if (filename.endsWith(".html")) {
        return (
          <div className="border border-gray-200 rounded-md p-2 bg-white shadow-sm transition-transform duration-200 ease-in-out">
            <HTMLwithJSViewer content={decodedContent} jobId={job_id} />
          </div>
        );
      }

      if (filename.endsWith(".csv")) {
        return (
          <div className="border border-gray-200 rounded-md p-2 bg-white shadow-sm transition-colors duration-200 ease-in-out hover:bg-gray-50">
            <CSVtableViewer csvContent={decodedContent} filename={filename} />
          </div>
        );
      }

      if ([".json", ".tex", ".txt"].some((ext) => filename.endsWith(ext))) {
        return (
          <div className="border border-gray-200 rounded-md p-4 bg-white shadow-sm font-mono text-sm whitespace-pre-wrap transition duration-200 ease-in-out">
            <PlaintextViewer content={decodedContent} filename={filename} />
          </div>
        );
      }

      if (filename.endsWith(".apkg")) {
        if (!ankiFile) return <div>Loading deck...</div>;
        return (
          <div className="border border-gray-200 rounded-md p-4 bg-white shadow-sm transition-all duration-200 ease-in-out">
            <AnkiDeckViewer file={ankiFile} jobId={job_id} />
          </div>
        );
      }

      return (
        <div className="border border-gray-200 rounded-md p-4 bg-white shadow-sm font-mono text-sm whitespace-pre-wrap transition duration-200 ease-in-out">
          <PlaintextViewer content={decodedContent} filename={filename} />
        </div>
      );
    },
    [optimisticUpdates, editMode, editingState, activeTab, handleSaveMarkdown, job_id, ankiFile],
  );

  return (
    <motion.div
      className="processed-results-section bg-gradient-to-b from-white via-gray-50 to-gray-100 flex flex-col overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <ErrorBoundary componentName="ProcessedDocumentResultsViewer">
        <div
          style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", overflow: "hidden" }}
        >
          <div
            style={{ padding: "15px", borderBottom: "1px solid #ddd", flexShrink: 0 }}
            className="px-1 sm:px-4 lg:px-16 bg-white"
          >
            <div
              className="flex justify-between items-center flex-wrap gap-2"
              style={{ paddingRight: "48px" }}
            >
              <h3 className="text-lg sm:text-2xl font-bold mb-2 lg:mb-0 text-gray-800">
                Generated Outputs:
              </h3>
              <div className="flex items-center">
                <div className="flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto px-0">
                  {onGenerateMore && (
                    <Tooltip title="Generate additional documents">
                      <Button
                        type="primary"
                        icon={<PlusCircleOutlined />}
                        onClick={onGenerateMore}
                        className="hidden md:inline-flex bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md transition-all duration-200 ease-in-out"
                      >
                        Generate More
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Download all files">
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadZipWrapper}
                      loading={isDownloading}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md transition-all duration-200 ease-in-out"
                    >
                      Download All (ZIP)
                    </Button>
                  </Tooltip>
                  <Tooltip title="Refresh files">
                    <Button
                      icon={<ReloadOutlined spin={isRefreshing} />}
                      onClick={handleManualRefresh}
                      disabled={isRefreshing}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition-all duration-200 ease-in-out"
                    >
                      Refresh Files
                    </Button>
                  </Tooltip>
                  {renderCustomerServiceRequest()}
                  <Button
                    icon={<MenuOutlined />}
                    onClick={() => setMobileMenuVisible(true)}
                    className="block lg:hidden mr-8 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-all duration-200 ease-in-out"
                  >
                    Select File
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Layout
            style={{
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              flex: "1",
            }}
          >
            <Content className="bg-white p-4 rounded-lg shadow-lg border-t border-gray-200 transition-all duration-200 ease-in-out">
              {activeTab && activeTab.endsWith(".md") && (
                <div className="mb-4 flex flex-wrap gap-2 p-0 sm:p-4">
                  {!editMode && (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(activeTab)}
                      type="primary"
                      className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-md transition-all duration-200 ease-in-out"
                    >
                      Edit Markdown
                    </Button>
                  )}
                  {hasRevisions && (
                    <Button
                      icon={<HistoryOutlined />}
                      onClick={() => handleViewRevisions(activeTab)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md transition-all duration-200 ease-in-out"
                    >
                      View Revisions
                    </Button>
                  )}
                </div>
              )}

              <AnimatePresence mode="wait">
                {activeTab && (
                  <motion.div
                    key={activeTab.endsWith(".apkg") ? activeTab : `${activeTab}-${editMode}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ minHeight: "500px", paddingTop: "8px" }}
                  >
                    {currentFileContent && renderFileContent(activeTab, currentFileContent)}
                  </motion.div>
                )}
              </AnimatePresence>
              {(jobSummaryLoading || filesLoading || isValidatingJobSummary) &&
                filteredFiles.length === 0 && (
                  <div
                    className="flex justify-center items-center h-64"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "64px",
                    }}
                  >
                    <Spin size="large" />
                  </div>
                )}
              {(fetchError || storeError) && (
                <div className="text-center text-red-500 mt-4">
                  <p>{fetchError || storeError}</p>
                  <Button
                    onClick={handleManualRefresh}
                    type="primary"
                    className="mt-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md transition-all duration-200 ease-in-out"
                  >
                    Retry
                  </Button>
                </div>
              )}
              {filteredFiles.length === 0 &&
                !isProcessingDocument &&
                !jobSummaryLoading &&
                !filesLoading &&
                !isValidatingJobSummary &&
                !fetchError &&
                !storeError && (
                  <div className="text-center text-gray-500 mt-4">
                    <p>No processed files found. Please check back later.</p>
                  </div>
                )}
            </Content>
          </Layout>
        </div>
        <Drawer
          title="File Revisions"
          placement="right"
          onClose={() => setRevisionsDrawerVisible(false)}
          open={revisionsDrawerVisible}
          width={800}
        >
          {fileRevisionsLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" />
            </div>
          ) : (
            <List
              dataSource={fileRevisionsData.filter((revision) => revision.filename === activeTab)}
              renderItem={(revision: FileRevision, index: number) => (
                <List.Item
                  key={revision.id}
                  className="border-b border-gray-200 py-4 hover:bg-gray-50 transition-all duration-200 ease-in-out"
                >
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-2 flex-wrap">
                      <Text strong className="text-lg mb-2 lg:mb-0 text-gray-800">
                        Revision{" "}
                        {fileRevisionsData.filter((rev) => rev.filename === activeTab).length -
                          index}
                      </Text>
                      <Space className="w-full lg:w-auto flex-wrap">
                        <Text type="secondary" className="text-gray-600">
                          {new Date(revision.createdAt).toLocaleString()}
                        </Text>
                        <Button
                          icon={<RollbackOutlined />}
                          onClick={() => handleRevertToRevision(revision)}
                          type="primary"
                          className="bg-red-500 hover:bg-red-600 text-white font-medium rounded-md transition-all duration-200 ease-in-out"
                        >
                          Revert to this Revision
                        </Button>
                      </Space>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto transition-shadow duration-200 ease-in-out">
                      {renderRevisionContent(
                        revision,
                        index,
                        fileRevisionsData.filter((rev) => rev.filename === activeTab),
                      )}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Drawer>
        {renderMobileMenu()}
      </ErrorBoundary>
      <style jsx>{`
        @media (max-width: 600px) and (orientation: portrait) {
          .processed-results-section {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            padding: 0 8px !important;
            border-radius: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            transform: scale(0.67) !important;
            transform-origin: top left !important;
            overflow: hidden !important;
          }
          .processed-results-section > * {
            transform: scale(1.49) !important;
            transform-origin: top left !important;
          }
          .processed-results-section .ant-btn {
            max-width: 150px !important;
          }
          .processed-results-section > div:first-child {
            padding: 4px 8px !important;
            border-bottom: 1px solid #ddd !important;
          }
          .processed-results-section h3.text-2xl {
            font-size: 1.25rem !important;
            line-height: 1.75rem !important;
          }
          .processed-results-section h3 {
            margin-bottom: 4px !important;
            line-height: 1.2 !important;
          }
          .processed-results-section .flex.justify-between {
            gap: 1px !important;
            margin-bottom: 2px !important;
          }
          .processed-results-section [class*='bg-green-500'] {
            display: none !important;
          }
          .processed-results-section .flex.items-center > .flex.flex-wrap {
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)) !important;
            gap: 2px !important;
            padding: 2px 4px !important;
            width: 100% !important;
          }
          .processed-results-section .ant-btn {
            padding: 2px 4px !important;
            font-size: 0.75rem !important;
            height: 24px !important;
            min-height: 24px !important;
          }
          .processed-results-section .ant-layout-content {
            padding: 2px !important;
          }
          .processed-results-section .mb-4 {
            margin-bottom: 2px !important;
          }
          .processed-results-section .p-4 {
            padding: 2px !important;
          }
        }

        .processed-results-section .ant-btn {
          min-width: fit-content !important;
          font-size: 12px !important;
          padding: 4px 8px !important;
          height: auto !important;
          white-space: normal !important;
          text-align: center !important;
          line-height: 1.2 !important;
        }

        @media (max-width: 640px) {
          .processed-results-section .ant-btn {
            flex: 1 1 auto !important;
            max-width: calc(50% - 4px) !important;
          }

          .processed-results-section .ant-btn-text {
            flex: 0 0 auto !important;
          }
        }

        @media (max-width: 600px) {
          .processed-results-section .flex.justify-between {
            padding: 0 4px !important;
          }

          .processed-results-section .flex.items-center {
            margin-top: 4px !important;
          }

          .processed-results-section .flex.items-center > .flex.flex-wrap {
            gap: 4px !important;
            justify-content: space-between !important;
          }

          .processed-results-section h3.text-lg {
            margin-bottom: 8px !important;
          }

          .processed-results-section .ant-btn {
            font-size: 12px !important;
            padding: 4px 8px !important;
            height: 32px !important;
            flex: 1 1 calc(50% - 4px) !important;
            max-width: calc(50% - 4px) !important;
          }

          .processed-results-section h3.text-lg {
            font-size: 16px !important;
            line-height: 24px !important;
            margin-bottom: 8px !important;
          }

          .processed-results-section > div:first-child {
            padding: 8px 4px !important;
          }

          .processed-results-section .ant-layout-content {
            padding: 8px !important;
          }
        }

        .markdown-editor-container {
          padding: 24px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .markdown-editor-container .rc-md-editor {
          height: calc(100vh - 200px) !important;
          min-height: 400px;
        }

        .markdown-editor-container .rc-md-editor .editor-container {
          height: calc(100% - 40px) !important;
        }
      `}</style>
    </motion.div>
  );
};

export default ProcessedDocumentResultsViewer;
